import { useEffect, useState } from 'react';
import { MerchReportService } from '../../../services/merchReport.service';
import { CostingService } from '../../../services/costing.service';
import { IOrderDashboardRow } from '../../../interfaces/garments/merchReport.interface';
import { ProductionOutput } from '../../../interfaces/costing.interface';

export interface ThreadOrders {
    /** null until the first answer (or when the caller may not read it). */
    rows: IOrderDashboardRow[] | null;
    loading: boolean;
    error: boolean;
    truncated: boolean;
}

/**
 * The live orders for the Thread board, from the order dashboard endpoint: ONE request, no per-order
 * calls. Pass `enabled=false` for a caller without MERCH_REPORT_VIEW and nothing is requested.
 */
export function useThreadOrders(institutionId: string, enabled: boolean): ThreadOrders {
    const [state, setState] = useState<ThreadOrders>({ rows: null, loading: enabled, error: false, truncated: false });

    useEffect(() => {
        if (!enabled || !institutionId) { setState({ rows: null, loading: false, error: false, truncated: false }); return; }
        let live = true;
        setState(s => ({ ...s, loading: true, error: false }));
        MerchReportService.getOrderDashboard(institutionId)
            .then(r => { if (live) setState({ rows: r.rows ?? [], loading: false, error: false, truncated: !!r.truncated }); })
            .catch(() => { if (live) setState({ rows: null, loading: false, error: true, truncated: false }); });
        return () => { live = false; };
    }, [institutionId, enabled]);

    return state;
}

/**
 * Production output rows for a filter, only when `params` is set (the caller holds COSTING_VIEW).
 * A failure leaves `null`, which the screens read as "no figures", never as zero.
 */
export function useProductionOutputs(
    institutionId: string,
    params: { buyerOrderId?: string; from?: string; to?: string } | null,
): ProductionOutput[] | null {
    const [outputs, setOutputs] = useState<ProductionOutput[] | null>(null);
    const key = params ? `${params.buyerOrderId ?? ''}|${params.from ?? ''}|${params.to ?? ''}` : '';

    useEffect(() => {
        if (!params || !institutionId) { setOutputs(null); return; }
        let live = true;
        CostingService.listOutputs(institutionId, params)
            .then(o => { if (live) setOutputs(o); })
            .catch(() => { if (live) setOutputs(null); });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId, key]);

    return outputs;
}
